import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";

const CountdownRing = ({
  seconds = 30,
  size = 84,        // ring size
  stroke = 2,      // ring thickness
  onComplete,
}) => {
  const [timeLeft, setTimeLeft] = useState(seconds);
  const onCompleteRef = useRef(onComplete);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    setTimeLeft(seconds);
    if (!seconds) return;

    const id = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) {
          clearInterval(id);
          onCompleteRef.current?.();
          return 0;
        }
        return t - 1;
      });
    }, 1000);

    return () => clearInterval(id);
  }, [seconds]);

  const radius = useMemo(() => (size - stroke) / 2, [size, stroke]);
  const circumference = useMemo(() => 2 * Math.PI * radius, [radius]);

  const progress = useMemo(() => {
    return seconds === 0 ? 0 : timeLeft / seconds;
  }, [timeLeft, seconds]);

  const dashOffset = useMemo(() => {
    return circumference * (1 - progress);
  }, [circumference, progress]);

  const center = size / 2;

  return (
    <Box
      sx={{
        position: "relative",
        width: size,
        height: size,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <defs>
          {/* Gradient stroke (like your red ring fade) */}
          <linearGradient id="ringGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="rgba(255,0,0,0.15)" />
            <stop offset="55%" stopColor="rgba(255,0,0,0.6)" />
            <stop offset="100%" stopColor="rgba(255,0,0,1)" />
          </linearGradient>
        </defs>

        {/* Background ring */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="rgba(255,0,0,0.12)"
          strokeWidth={stroke}
        />

        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="url(#ringGradient)"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          transform={`rotate(-90 ${center} ${center})`} // start from top
          style={{
            transition: "stroke-dashoffset 700ms linear",
          }}
        />
      </svg>

      {/* Center text */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          userSelect: "none",
        }}
      >
        <Typography sx={{ fontWeight: 800, fontSize: 14, lineHeight: 1 }}>
          {timeLeft}
        </Typography>
      </Box>
    </Box>
  );
};

export default CountdownRing;
